"use client";

import { useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Download, FileUp, Loader2, Rocket, Trash2, TrendingUp } from "lucide-react";
type AlertColor = "success" | "info" | "warning" | "error";
import { useBatchTasks } from "../context";
import { useBatchSubmission } from "../hooks/useBatchSubmission";
import BatchResultDialog from "./BatchResultDialog";
import { useLanguageStore } from "@/client/store/languageStore";
import { getBatchTemplateFilename } from "../utils/filenameLocales";

interface FeedbackState {
  severity: AlertColor;
  message: string;
  details?: string[];
  timestamp: number;
}

const FILE_ACCEPT = ".csv,text/csv";
const TEMPLATE_CONTENT = `prompt
提示词示例 1
提示词示例 2`;

const BatchTaskToolbar: React.FC = () => {
  const { tasks, selectedTaskIds, importFromCsv, clearTasks, removeTasks } = useBatchTasks();
  const language = useLanguageStore((state) => state.language);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [importing, setImporting] = useState(false);
  const [feedback, setFeedback] = useState<FeedbackState | null>(null);
  const [progressDialogOpen, setProgressDialogOpen] = useState(false);
  const { submitTasks, submitting } = useBatchSubmission();

  const handleSelectFile = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange: React.ChangeEventHandler<HTMLInputElement> = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setImporting(true);
    const summary = await importFromCsv(file);
    const severity: AlertColor =
      summary.errors.length > 0 ? "error" : summary.warnings.length > 0 ? "warning" : "success";
    const message =
      summary.errors.length > 0
        ? `导入失败：${summary.errors[0]}`
        : summary.imported > 0
          ? `成功导入 ${summary.imported} 条 Prompt`
          : "未导入新的 Prompt";
    let details: string[] | undefined;
    if (summary.errors.length > 1) {
      details = summary.errors.slice(1);
    } else if (summary.errors.length === 0 && summary.warnings.length) {
      details = summary.warnings;
    }
    const detailList = details && details.length ? details : undefined;
    setFeedback({
      severity,
      message,
      details: detailList,
      timestamp: Date.now(),
    });
    setImporting(false);
    // 清空 input 的值，避免同一文件重复选择无效
    event.target.value = "";
  };

  const handleClear = () => {
    clearTasks();
    setFeedback({
      severity: "info",
      message: "已清空所有导入的任务。",
      timestamp: Date.now(),
    });
  };

  const handleDeleteSelected = () => {
    const count = selectedTaskIds.length;
    if (!count) return;
    removeTasks(selectedTaskIds);
    setFeedback({
      severity: "info",
      message: `已删除 ${count} 个任务`,
      timestamp: Date.now(),
    });
  };

  const targetTaskIds = useMemo(() => {
    if (selectedTaskIds.length) {
      return selectedTaskIds;
    }
    return tasks.map((task) => task.id);
  }, [selectedTaskIds, tasks]);

  const submittableCount = useMemo(() => {
    if (targetTaskIds.length === 0) {
      return 0;
    }
    const targetSet = new Set(targetTaskIds);
    return tasks.reduce((acc, task) => {
      if (!targetSet.has(task.id)) return acc;
      return task.modelKey ? acc + 1 : acc;
    }, 0);
  }, [targetTaskIds, tasks]);

  const handleDownloadTemplate = () => {
    try {
      const blob = new Blob([TEMPLATE_CONTENT], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = getBatchTemplateFilename(language);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.warn("[BatchTaskToolbar] 下载模板失败", error);
    }
  };

  const handleOpenProgressDialog = () => {
    setProgressDialogOpen(true);
  };

  const handleCloseProgressDialog = () => {
    setProgressDialogOpen(false);
  };

  const triggerCsvDownload = (content: string, filename: string): boolean => {
    if (typeof window === "undefined") {
      return false;
    }
    try {
      const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      return true;
    } catch (error) {
      console.warn("[BatchTaskToolbar] 下载任务 CSV 失败", error);
      return false;
    }
  };

  const handleSubmitTasks = async () => {
    setFeedback(null);
    if (submittableCount === 0) {
      setFeedback({
        severity: "info",
        message: "请选择已配置模型的任务后再提交。",
        timestamp: Date.now(),
      });
      return;
    }

    if (submitting || importing) {
      return;
    }

    const result = await submitTasks({ taskIds: targetTaskIds });
    if (result.aborted) {
      setFeedback({
        severity: "info",
        message: "任务正在提交中，请稍后再试。",
        timestamp: Date.now(),
      });
      return;
    }

    if (result.error) {
      setFeedback({
        severity: "error",
        message: result.error,
        timestamp: Date.now(),
      });
      return;
    }

    if (result.csvContent && result.csvFilename) {
      triggerCsvDownload(result.csvContent, result.csvFilename);
    }

    const issueCount = result.failed + result.skipped;
    const severity: AlertColor =
      result.attempted === 0
        ? "info"
        : result.success > 0 && issueCount === 0
          ? "success"
          : result.success > 0
            ? "warning"
            : "error";

    const details: string[] = [];
    if (result.success > 0) {
      details.push(`成功提交 ${result.success} 个任务`);
    }
    if (result.failed > 0) {
      details.push(`提交失败 ${result.failed} 个任务`);
    }
    if (result.skipped > 0) {
      details.push(`跳过 ${result.skipped} 个任务（参数不完整或模型未配置）`);
    }
    if (result.csvRowCount > 0) {
      details.push(`已生成任务 ID CSV，共 ${result.csvRowCount} 条记录`);
    }

    const summaryMessage =
      result.attempted === 0
        ? "没有可提交的任务，请检查选择和参数。"
        : `提交完成：目标 ${result.totalTargets} 条，成功 ${result.success} 条。`;

    setFeedback({
      severity,
      message: summaryMessage,
      details: details.length ? details : undefined,
      timestamp: Date.now(),
    });
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
        <div>
          <div className="text-xl font-semibold">批量生成任务</div>
          <div className="text-sm text-muted-foreground mt-1">
            导入只包含 Prompt 的 CSV，随后批量配置模型参数、参考素材并统一提交。
          </div>
          <div className="text-xs text-muted-foreground mt-1">当前任务数：{tasks.length}</div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <input ref={fileInputRef} type="file" accept={FILE_ACCEPT} hidden onChange={handleFileChange} />
          <Button onClick={handleSelectFile} disabled={importing || submitting}>
            {importing ? <Loader2 className="mr-2 size-4 animate-spin" /> : <FileUp className="mr-2 size-4" />}
            导入 CSV
          </Button>
          <Button variant="outline" onClick={handleDownloadTemplate} disabled={importing || submitting}>
            <Download className="mr-2 size-4" /> 下载模板
          </Button>
          <Button variant="outline" onClick={handleOpenProgressDialog}>
            <TrendingUp className="mr-2 size-4" /> 查询进度 / 打包下载
          </Button>
          <Tooltip>
            <TooltipTrigger asChild>
              <span>
                <Button onClick={handleSubmitTasks} disabled={submitting || importing || submittableCount === 0}>
                  {submitting ? <Loader2 className="mr-2 size-4 animate-spin" /> : <Rocket className="mr-2 size-4" />}
                  提交任务
                </Button>
              </span>
            </TooltipTrigger>
            <TooltipContent>
              {submittableCount === 0 ? "请选择已配置模型的任务后再提交" : "提交所选任务"}
            </TooltipContent>
          </Tooltip>
          <Button
            variant="outline"
            onClick={handleDeleteSelected}
            disabled={importing || submitting || selectedTaskIds.length === 0}
          >
            <Trash2 className="mr-2 size-4" /> 删除选定
          </Button>
          <Button variant="outline" onClick={handleClear} disabled={importing || submitting || tasks.length === 0}>
            <Trash2 className="mr-2 size-4" /> 清空列表
          </Button>
        </div>
      </div>

      {feedback ? (
        <Alert variant={feedback.severity === "error" ? "destructive" : "default"}>
          <AlertDescription>
            <div className="text-sm">{feedback.message}</div>
            {feedback.details?.map((line, index) => (
              <div key={`${feedback.timestamp}-${index}-${line}`} className="text-xs text-muted-foreground">
                {line}
              </div>
            ))}
          </AlertDescription>
        </Alert>
      ) : null}

      <BatchResultDialog open={progressDialogOpen} onClose={handleCloseProgressDialog} />
    </div>
  );
};

export default BatchTaskToolbar;
